import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Skeleton, message } from "antd";
import { GoCloudUpload } from "react-icons/go";
import { Modal, Button, InputGroup } from "../../../../UI";
import { useQuery } from "../../../../hooks";
import { AppConstants } from "../../../../constants";
import {
  createSubAdmin,
  getUser,
  updateUser,
  getAllHmos,
  getAllUsers,
} from "../../../../redux/actions";
import Container from "./style";

const ModalContent = (props = {}) => {
  const { activeUser, handleModalClose, onUserUpdated } = props;
  const queryUserId = useQuery().get("userId");
  const userId = activeUser?.id || queryUserId;
  const dispatch = useDispatch();
  const history = useHistory();
  const { gymLocations } = useSelector((s) => s.dashStats);

  const [loading, setLoading] = useState(false);
  const [hmos, setHmos] = useState([]);

  const [formData, setFormData] = useState(
    userId
      ? null
      : {
          name: "",
          email: "",
          phone: "",
          gender: "m",
          dob: "",
          height: "",
          weight: "",
          image: "",
          location_id: 1,
          role_id: 2,
          status: "active",
          emergency_contact_name: "",
          emergency_contact_phone: "",
          emergency_contact_relationship: "",
          hmo_id: "",
          hmo_number: "",
          hmo_status: "",
          hmo_expires_at: "",
        }
  );

  useEffect(() => {
    dispatch(getAllHmos()).then((res) => {
      if (res) setHmos(res);
    });

    if (userId) {
      getUser(userId).then((res) => {
        if (res && res.data) {
          const u = res.data;
          setFormData({
            name: u.name || "",
            email: u.email || "",
            phone: u.phone || "",
            gender: u.gender ? u.gender.toLowerCase() : "m",
            dob: u.dob ? u.dob.substring(0, 10) : "",
            height: u.height ? String(parseFloat(u.height)) : "",
            weight: u.weight ? String(parseFloat(u.weight)) : "",
            image: u.image || "",
            location_id: u.location_id || 1,
            role_id: u.role_id || 2,
            status: u.status || "active",
            emergency_contact_name: u.emergency_contact_name || "",
            emergency_contact_phone: u.emergency_contact_phone || "",
            emergency_contact_relationship: u.emergency_contact_relationship || "",
            hmo_id: u.hmo_id || "",
            hmo_number: u.hmo_number || "",
            hmo_status: u.hmo_status || "",
            hmo_expires_at: u.hmo_expires_at ? u.hmo_expires_at.substring(0, 10) : "",
          });
        }
      });
    }
  }, [userId, dispatch]);

  const handleInput = ({ target: { name, value } }) => {
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const handleNumericInput = ({ target: { name, value } }) => {
    // Normalize comma to dot and keep only numeric characters and a single decimal point
    let clean = value.replace(",", ".").replace(/[^0-9.]/g, "");
    const parts = clean.split(".");
    if (parts.length > 2) {
      clean = parts[0] + "." + parts.slice(1).join("");
    }
    setFormData((s) => ({ ...s, [name]: clean }));
  };

  const handleAddImg = ({ target: { files } }) => {
    if (files && files[0]) {
      const file = files[0];
      if (!file.type.startsWith("image/")) {
        message.error("Please upload a valid image file (PNG, JPG, JPEG).", 8);
        return;
      }
      if (file.size > 2000000) {
        message.info("Image size is too large (maximum 2MB)", 8);
        return;
      }
      setFormData((s) => ({ ...s, image: file }));
    }
  };

  const handleRemoveImg = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFormData((s) => ({ ...s, image: "" }));
  };

  const getImagePreviewUrl = () => {
    if (!formData || !formData.image) return "";
    if (typeof formData.image === "string") {
      if (formData.image.startsWith("http")) return formData.image;
      return `${AppConstants.imgBase}${formData.image}`;
    }
    if (formData.image instanceof File || formData.image instanceof Blob) {
      try {
        return URL.createObjectURL(formData.image);
      } catch (e) {
        return "";
      }
    }
    return "";
  };

  const imgUrl = getImagePreviewUrl();

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.phone) {
      message.error("Name, email, and phone number are required.");
      return;
    }

    // Validate decimal height if entered
    if (formData.height) {
      const h = parseFloat(formData.height);
      if (isNaN(h) || h <= 0) {
        message.error("Height must be a valid positive number (e.g. 175.5)");
        return;
      }
    }

    // Validate decimal weight if entered
    if (formData.weight) {
      const w = parseFloat(formData.weight);
      if (isNaN(w) || w <= 0) {
        message.error("Weight must be a valid positive number (e.g. 70.5)");
        return;
      }
    }

    try {
      setLoading(true);

      const payload = {
        ...formData,
        height: formData.height ? parseFloat(formData.height) : 0,
        weight: formData.weight ? parseFloat(formData.weight) : 0,
        location_id: Number(formData.location_id) || 1,
        hmo_id: formData.hmo_id ? Number(formData.hmo_id) : null,
      };

      if (userId) {
        payload.id = userId;
        const response = await dispatch(updateUser(userId, payload));
        if (response && response.success) {
          if (onUserUpdated) {
            onUserUpdated(response.data || payload);
          }
          if (handleModalClose) {
            handleModalClose();
          } else {
            history.goBack();
          }
        }
      } else {
        const action = dispatch(createSubAdmin({ ...payload, role_id: 2 }));
        const response = await action;
        if (response && response.status === true) {
          dispatch(getAllUsers("", true));
          if (handleModalClose) {
            handleModalClose();
          } else {
            history.goBack();
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Modal
        showModal={true}
        onClose={() =>
          handleModalClose ? handleModalClose() : history.goBack()
        }
        modalTitle={userId ? "Edit User Details" : "New User"}
        modalFooter={
          Boolean(formData) && (
            <Button form="subadmin-form" full loading={loading} type="submit">
              {userId ? "Save Changes" : "Create User"}
            </Button>
          )
        }
      >
        {formData ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            id="subadmin-form"
            name="subadmin-form"
            noValidate
          >
            <div className="form-section-title">👤 Personal Details</div>
            <div className="img-container">
              <label
                htmlFor="user-avatar-upload"
                className="upload-label"
                style={{
                  backgroundImage: imgUrl ? `url(${imgUrl})` : "none",
                }}
              >
                <div className="upload-content">
                  <GoCloudUpload />
                  <p>{imgUrl ? "Change Profile Picture" : "Upload Profile Picture"}</p>
                  <span className="sub-text">Max 2MB (JPG, PNG)</span>
                  <input
                    type="file"
                    id="user-avatar-upload"
                    name="image"
                    onChange={handleAddImg}
                    accept="image/png, image/jpeg, image/jpg"
                  />
                </div>
              </label>
              {imgUrl && (
                <div className="img-actions">
                  <button
                    type="button"
                    className="remove-img-btn"
                    onClick={handleRemoveImg}
                  >
                    Remove Photo
                  </button>
                </div>
              )}
            </div>
            <InputGroup
              label="Full Name"
              required={true}
              name="name"
              value={formData.name}
              onChange={handleInput}
            />
            <div className="form-grid-2">
              <InputGroup
                label="Email"
                type="email"
                required={true}
                name="email"
                value={formData.email}
                onChange={handleInput}
              />
              <InputGroup
                label="Phone Number"
                name="phone"
                type="tel"
                required={true}
                value={formData.phone}
                onChange={handleInput}
              />
            </div>

            <div className="form-grid-3">
              <InputGroup
                type="select"
                label="Gender"
                name="gender"
                required={true}
                value={formData.gender}
                onChange={handleInput}
                optionLists={
                  <>
                    <option value="m">Male</option>
                    <option value="f">Female</option>
                  </>
                }
              />
              <div>
                <label>Date of Birth</label>
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleInput}
                />
              </div>
              <div>
                <label>Gym Location</label>
                <select
                  name="location_id"
                  required={true}
                  onChange={handleInput}
                  value={formData.location_id}
                >
                  {Object.keys(gymLocations || {}).map((locationId) => (
                    <option key={locationId} value={locationId}>
                      {gymLocations[locationId]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {userId && (
              <div style={{ marginTop: 8 }}>
                <label>Account Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInput}
                >
                  <option value="active">Active</option>
                  <option value="banned">Banned</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            )}

            <div className="form-section-title">
              ⚖️ Physical Metrics (Height & Weight)
            </div>
            <div className="form-grid-2">
              <div>
                <label>Height (cm) - Decimals supported</label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="height"
                  value={formData.height}
                  onChange={handleNumericInput}
                  placeholder="e.g. 175.5"
                />
              </div>
              <div>
                <label>Weight (kg) - Decimals supported</label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="weight"
                  value={formData.weight}
                  onChange={handleNumericInput}
                  placeholder="e.g. 70.25"
                />
              </div>
            </div>

            <div className="form-section-title">🚨 Emergency Contact</div>
            <InputGroup
              label="Contact Name"
              name="emergency_contact_name"
              value={formData.emergency_contact_name}
              onChange={handleInput}
              placeholder="e.g. Jane Doe"
            />
            <div className="form-grid-2">
              <InputGroup
                label="Contact Phone"
                name="emergency_contact_phone"
                type="tel"
                value={formData.emergency_contact_phone}
                onChange={handleInput}
                placeholder="e.g. 08012345678"
              />
              <InputGroup
                label="Relationship"
                name="emergency_contact_relationship"
                value={formData.emergency_contact_relationship}
                onChange={handleInput}
                placeholder="e.g. Spouse, Parent, Sibling"
              />
            </div>

            <div className="form-section-title">🏥 HMO Information</div>
            <div className="form-grid-2">
              <div>
                <label>HMO Provider</label>
                <select
                  name="hmo_id"
                  value={formData.hmo_id}
                  onChange={handleInput}
                >
                  <option value="">None / Not Registered</option>
                  {hmos.map((hmo) => (
                    <option key={hmo.id} value={hmo.id}>
                      {hmo.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>HMO Number</label>
                <input
                  type="text"
                  name="hmo_number"
                  value={formData.hmo_number}
                  onChange={handleInput}
                  placeholder="e.g. HMO-12345"
                />
              </div>
            </div>

            <div className="form-grid-2" style={{ marginTop: 8 }}>
              <div>
                <label>HMO Status</label>
                <select
                  name="hmo_status"
                  value={formData.hmo_status}
                  onChange={handleInput}
                >
                  <option value="">None</option>
                  <option value="pending">Pending Approval</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label>HMO Expiration Date</label>
                <input
                  type="date"
                  name="hmo_expires_at"
                  value={formData.hmo_expires_at}
                  onChange={handleInput}
                />
              </div>
            </div>
          </form>
        ) : (
          <div className="loading-container">
            <Skeleton active />
            <Skeleton active />
          </div>
        )}
      </Modal>
    </Container>
  );
};

export default ModalContent;
